// ============================================================
// BASIM YARDIMCILARI (Faz C, 2026-09-03) — saf fonksiyonlar. Çoğu Basim.js'ten
// birebir taşındı; yeni olanlar: profilKova, denemeZorlukKur,
// konuZorlukQueryOlustur, taslakEsle, zorlukEnumdanProfil.
// ============================================================
import { DERSLER, kazanimSozleri, SINAV_DERSLERI, sikHarfleri, DERS_BASLIK } from '../../lib/dersler.js';
import { satildiMi } from '../../lib/satis';
import { HATA_MESAJLARI, ZORLUK_PROFILLERI, TASLAK_SURUM, BASLANGIC_KONU_TEST } from './sabitler';

// Ön kontrol raporunu (basim/on-kontrol) uyarı şeridi satırlarına indirger.
// Faz C: konu testi kanalının dilim/kova raporu ve `uygulanan` ekosu da okunur —
// sunucu bir alanı işlemediyse (eski sürüm) şerit bunu AÇIKÇA söyler.
export function onKontrolOzetle(r, body) {
  const satirlar = [];
  const nolar = Array.isArray(r.public_no_listesi) ? r.public_no_listesi : [];
  const tekrar = nolar.length - new Set(nolar).size;
  if (Number(r.eksik_adet) > 0) {
    satirlar.push({ tip: 'uyari', metin: `İstenen ${r.istenen_adet} sorunun ${r.eksik_adet} tanesi havuzda yok — ${r.soru_sayisi} soru basılacak.` });
  }
  if (tekrar > 0) satirlar.push({ tip: 'uyari', metin: `${tekrar} soru mükerrer (aynı soru listede birden fazla).` });
  // Cevap QC (2026-09-15, KAPI C): kör denetim ya da öğretmenin kör onayı anahtarla
  // uyuşmadıysa UYARI; yoksa yalnız kapsama bilgisi (denetlenmemiş soru uyarı değildir —
  // korpusun çoğu henüz denetlenmedi, her basımda uyarı gürültü olurdu).
  const qc = r.cevap_qc_ozeti;
  if (qc && typeof qc === 'object') {
    const uy = Array.isArray(qc.uyusmazlik) ? qc.uyusmazlik : [];
    if (uy.length > 0) {
      satirlar.push({ tip: 'uyari', metin: `Cevap QC: ${uy.length} soruda kör denetim anahtarla uyuşmadı (${uy.slice(0, 12).map(n => '#' + n).join(', ')}${uy.length > 12 ? '…' : ''}) — basmadan önce kontrol edin.` });
    } else {
      const top = (Number(qc.onayli) || 0) + (Number(qc.yok) || 0);
      satirlar.push({ tip: 'bilgi', metin: `Cevap QC: ${Number(qc.onayli) || 0}/${top} soru kör denetimden geçti; uyuşmazlık yok.` });
    }
  }
  const soz = kazanimSozleri(body && body.filtre && body.filtre.mufredat);
  for (const d of (Array.isArray(r.dilim_raporu) ? r.dilim_raporu : [])) {
    if (d.eksik > 0) {
      satirlar.push({ tip: 'uyari', metin: `${d.tip === 'kazanim' ? soz.Ad : d.tip === 'konu' ? 'Konu' : 'Ders'} ${d.etiket}: ${d.gerceklesen}/${d.hedef} — ${d.eksik} soru havuzda yok${r.dilim_asimi ? `, diğer ${soz.adlardan} tamamlandı` : ''}.` });
    }
  }
  const kr = r.zorluk_kota_raporu;
  if (kr && typeof kr === 'object') {
    const borc = ['kolay', 'orta', 'zor'].reduce((a, k) => a + (Number(kr[k]?.borc) || 0), 0);
    const ozet = ['kolay', 'orta', 'zor'].map(k => `${k} ${kr[k]?.gerceklesen ?? 0}/${kr[k]?.hedef ?? 0}`).join(' · ');
    satirlar.push({ tip: borc > 0 ? 'uyari' : 'bilgi', metin: `Zorluk: ${ozet}${borc > 0 ? ` — ${borc} soru komşu kovadan tamamlandı (havuz ince)` : ''}.` });
  }
  const gonderilen = (body && body.filtre) || {};
  const u = r.uygulanan || {};
  if (gonderilen.zorlukKota && !u.zorlukKota) {
    satirlar.push({ tip: 'uyari', metin: 'Sunucu zorluk profilini UYGULAMADI — çıktı zorluk kısıtı olmadan seçildi (sunucu sürümü eski olabilir).' });
  }
  if (gonderilen.kazanimDagilimi && !u.kazanimDagilimi) {
    satirlar.push({ tip: 'uyari', metin: `Sunucu ${soz.ad} dağılımını UYGULAMADI — adetler ${soz.adlar} bazında bölünmedi (sunucu sürümü eski olabilir).` });
  }
  if (gonderilen.tarih_min || gonderilen.tarih_max) {
    if (!u.tarih) {
      satirlar.push({ tip: 'uyari', metin: 'Sunucu tarih aralığını UYGULAMADI — sorular tüm havuzdan seçildi (sunucu sürümü eski olabilir).' });
    } else {
      satirlar.push({ tip: 'bilgi', metin: `Tarih: ${gonderilen.tarih_min || 'başlangıç'} → ${gonderilen.tarih_max || 'bugün'} aralığında üretilen sorulardan seçildi.` });
    }
  }
  if (gonderilen.dersPlani && !u.dersPlani) {
    satirlar.push({ tip: 'uyari', metin: 'Sunucu ders planını UYGULAMADI — çok dersli genel deneme tek blok basılır (sunucu sürümü eski olabilir).' });
  }
  // Hafta süzgeci (TYMM Öğrenme Kanıtı, 2026-10-09): ders başına kaç ÖÇ tamamlanmış, son ÖÇ.
  if (gonderilen.hafta) {
    if (!u.hafta) {
      satirlar.push({ tip: 'uyari', metin: 'Sunucu hafta süzgecini UYGULAMADI — sorular sınıfın tüm öğrenme çıktılarından seçildi (sunucu sürümü eski olabilir).' });
    } else {
      const hk = Object.entries(r.hafta_kapsami || {});
      satirlar.push({ tip: 'bilgi', metin: `${gonderilen.hafta}. haftaya kadar tamamlanan öğrenme çıktılarından seçildi`
        + (hk.length ? ': ' + hk.map(([d, x]) => `${DERSLER.find(y => y.value === d)?.label || d} ${x.oc_tamam}/${x.oc_toplam} ÖÇ${x.son_oc ? ` (son ${x.son_oc.kod})` : ''}`).join(' · ') : '') + '.' });
    }
  }
  if (Array.isArray(r.ders_raporu) && r.ders_raporu.length > 0) {
    satirlar.push({ tip: r.ders_raporu.some(d => d.eksik > 0) ? 'uyari' : 'bilgi', metin: 'Genel deneme (ÖSYM çok-testli): '
      + r.ders_raporu.map(d => `${d.ad || d.ders} ${d.gerceklesen}/${d.hedef}${d.eksik > 0 ? ' ⚠' : ''}`).join(' · ') });
  }
  if (Number(r.gorselli_soru) > 0) {
    satirlar.push({ tip: 'bilgi', metin: `${r.gorselli_soru} soruda görsel var (TikZ ${r.tikz_soru || 0}, SVG ${r.svg_soru || 0}).` });
  }
  const dersler = Object.entries(r.ders_dagilimi || {});
  if (dersler.length > 1) {
    satirlar.push({ tip: 'bilgi', metin: 'Ders dağılımı: ' + dersler.map(([d, n]) => `${DERSLER.find(x => x.value === d)?.label || d} ${n}`).join(' · ') });
  }
  if (r.tahmini_sayfa_aralik) {
    satirlar.push({ tip: 'bilgi', metin: `Tahmini ${r.tahmini_sayfa_aralik} sayfa (${r.layout_profil || 'profil'}).` });
  }
  return {
    soru_sayisi: r.soru_sayisi,
    istenen_adet: r.istenen_adet ?? null,
    sabitlendi: !!(body && body.filtre && Array.isArray(r.soru_id_listesi) && r.soru_id_listesi.length > 0),
    satirlar,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TAM DENEME MANİFESTOSU — saf yardımcılar (2026-08-20)
// ─────────────────────────────────────────────────────────────────────────────
export function manifestSlotlari(m) {
  const out = [];
  for (const t of (m?.testler || [])) {
    for (const d of (t.dersler || [])) {
      (d.sorular || []).forEach((x, i) => out.push({ test: t, ders: d.ders, idx: i, soru: x }));
    }
  }
  return out;
}
export function bosSlotSayisi(m) {
  return manifestSlotlari(m).filter(x => !x.soru).length;
}
export function manifestPublicNolar(m) {
  return manifestSlotlari(m).filter(x => x.soru).map(x => x.soru.public_no);
}
/** Sunucuya giden yalın biçim — 120 slot yalnız numaralarla taşınır. */
export function manifestYalinlastir(m) {
  return {
    sinav: m.sinav,
    kitapcik_turu: m.kitapcik_turu,
    testler: (m.testler || []).map(t => ({
      kod: t.kod,
      dersler: (t.dersler || []).map(d => ({
        ders: d.ders,
        public_no: (d.sorular || []).map(x => (x ? x.public_no : null)),
      })),
    })),
  };
}

/** { ders:{kolay,orta,zor} } -> "ders:k-o-z,ders:k-o-z" (GET gövdesi yok). */
export function zorlukQueryOlustur(z) {
  const parcalar = Object.entries(z || {})
    .filter(([, u]) => u && ['kolay', 'orta', 'zor'].every(k => Number.isInteger(u[k])))
    .map(([ders, u]) => `${ders}:${u.kolay}-${u.orta}-${u.zor}`);
  return parcalar.length > 0 ? parcalar.join(',') : '';
}

// ─────────────────────────────────────────────────────────────────────────────
// KONU BAŞINA ADET (2026-09-03 gece, kullanıcı kararı "tam N soru, sert"):
// konu ayarı 'zor' (eski dizgi) ya da { kova?, adet? } nesnesi — ikisi de okunur
// (eski şablon/taslak bozulmaz). Yazarken yalnız kova varsa dizgi, adet varsa nesne
// (backend denemeZorlukDogrula iki biçimi de kabul eder). `adet` = o konudan TAM N
// soru, ders kotası İÇİNDE; ders içi Σadet kotayı aşamaz (panel kırpar, sunucu 400).
// ─────────────────────────────────────────────────────────────────────────────
const KOVALAR = ['kolay', 'orta', 'zor'];
export function konuAyarCoz(v) {
  if (typeof v === 'string') return { kova: KOVALAR.includes(v) ? v : '', adet: null };
  if (!v || typeof v !== 'object') return { kova: '', adet: null };
  const kova = KOVALAR.includes(v.kova) ? v.kova : '';
  const n = parseInt(v.adet, 10);
  return { kova, adet: Number.isInteger(n) && n > 0 ? n : null };
}
export function konuAyarYaz({ kova, adet }) {
  const k = KOVALAR.includes(kova) ? kova : '';
  const n = Number.isInteger(adet) && adet > 0 ? adet : null;
  if (!k && n == null) return null;
  if (n == null) return k;
  return k ? { kova: k, adet: n } : { adet: n };
}
/** {konu: v} → geçerli girişler normalize (geçersiz/boş düşer). */
export function konuAyarlariTemizle(konular) {
  const t = {};
  for (const [konu, v] of Object.entries(konular || {})) {
    const y = konuAyarYaz(konuAyarCoz(v));
    if (y != null) t[konu] = y;
  }
  return t;
}
/** Yalnız kovalı konular {konu: kova} — slot-doldur `konuOverride` (eski sözleşme). */
export function konuKovaHaritasi(konular) {
  const t = {};
  for (const [konu, v] of Object.entries(konular || {})) { const { kova } = konuAyarCoz(v); if (kova) t[konu] = kova; }
  return t;
}
/** Adet verilmiş konu adları — slot-doldur `konuSabit`. */
export function konuSabitListesi(konular) {
  return Object.entries(konular || {}).filter(([, v]) => konuAyarCoz(v).adet != null).map(([k]) => k);
}
export function konuAdetToplami(konular) {
  return Object.values(konular || {}).reduce((a, v) => a + (konuAyarCoz(v).adet || 0), 0);
}

/** { ders:{konu:ayar} } -> JSON metni (GET `denemeZorlukKonu`); boşsa ''. */
export function konuZorlukQueryOlustur(kz) {
  const temiz = {};
  for (const [ders, konular] of Object.entries(kz || {})) {
    const k = konuAyarlariTemizle(konular);
    if (Object.keys(k).length > 0) temiz[ders] = k;
  }
  return Object.keys(temiz).length > 0 ? JSON.stringify(temiz) : '';
}

/**
 * Her dersin üçlü toplamı kendi kotasına eşit mi. Backend zaten 400'ler ama
 * panel isteği HİÇ göndermeden söylemeli — 120 soruluk bir istek boşa gitmesin.
 * Kota kaynağı fizibilite yanıtı (format tek doğru kaynak orada).
 */
export function zorlukGecerliMi(z, fizibilite) {
  const kotalar = {};
  for (const t of (fizibilite?.testler || [])) for (const d of t.dersler) kotalar[d.ders] = d.kota;
  for (const [ders, u] of Object.entries(z || {})) {
    if (!(ders in kotalar)) return false;
    if (!u || !['kolay', 'orta', 'zor'].every(k => Number.isInteger(u[k]) && u[k] >= 0)) return false;
    if (u.kolay + u.orta + u.zor !== kotalar[ders]) return false;
  }
  return true;
}

/**
 * Sunucuya giden `ayarlar.denemeZorluk` (Faz A sözleşmesi):
 *   { ders: { kolay, orta, zor, konular: { konu: kova } } }
 * Ders kotası üçlüsü yalnız geçerliyse (zorlukGecerliMi) gider; konu ayarı olan
 * ders üçlüsüz de gidebilir (backend varsayılan eşit bölünme uygular).
 */
export function denemeZorlukKur(dersZorluk, konuZorluk, fizibilite) {
  const sonuc = {};
  const ucluGecerli = zorlukGecerliMi(dersZorluk, fizibilite);
  if (ucluGecerli) {
    for (const [ders, u] of Object.entries(dersZorluk || {})) {
      sonuc[ders] = { kolay: u.kolay, orta: u.orta, zor: u.zor };
    }
  }
  for (const [ders, konular] of Object.entries(konuZorluk || {})) {
    const k = konuAyarlariTemizle(konular);
    if (Object.keys(k).length === 0) continue;
    sonuc[ders] = { ...(sonuc[ders] || {}), konular: k };
  }
  return Object.keys(sonuc).length > 0 ? sonuc : null;
}

/** Kotayı üç kovaya böler — backend zorlukKotalari() ile AYNI kural. */
export function esitKova(adet) {
  const taban = Math.floor(adet / 3);
  const kota = { kolay: taban, orta: taban, zor: taban };
  const artik = adet - taban * 3;
  if (artik >= 1) kota.orta += 1;
  if (artik >= 2) kota.kolay += 1;
  return kota;
}

/** Preset dağıtıcı: ağırlıklara göre en büyük artık yöntemiyle böler. */
export function agirlikliKova(adet, agirlik) {
  const toplamA = agirlik.kolay + agirlik.orta + agirlik.zor;
  const ham = {
    kolay: (adet * agirlik.kolay) / toplamA,
    orta: (adet * agirlik.orta) / toplamA,
    zor: (adet * agirlik.zor) / toplamA,
  };
  const kota = { kolay: Math.floor(ham.kolay), orta: Math.floor(ham.orta), zor: Math.floor(ham.zor) };
  let kalan = adet - (kota.kolay + kota.orta + kota.zor);
  const sirali = ['kolay', 'orta', 'zor'].sort((a, b) => (ham[b] - kota[b]) - (ham[a] - kota[a]));
  for (let i = 0; kalan > 0; i += 1, kalan -= 1) kota[sirali[i % 3]] += 1;
  return kota;
}

/** Konu testi profili -> {kolay,orta,zor} (filtre.zorlukKota); 'karisik' -> null. */
export function profilKova(profilId, adet) {
  const p = ZORLUK_PROFILLERI.find(x => x.id === profilId);
  if (!p || !p.agirlik) return null;
  const n = Math.max(1, Math.floor(Number(adet) || 0));
  if (p.id === 'dengeli') return esitKova(n);
  return agirlikliKova(n, p.agirlik);
}

/** Eski filtre ENUM dizisi (kolay/orta/zor/uzman) -> profil id (şablon göçü). */
export function zorlukEnumdanProfil(z) {
  const s = new Set(Array.isArray(z) ? z : []);
  if (s.size === 0) return 'karisik';
  const kolay = s.has('kolay'); const orta = s.has('orta'); const zor = s.has('zor') || s.has('uzman');
  if (kolay && !orta && !zor) return 'kolay';
  if (!kolay && !orta && zor) return 'tumu_zor';
  if (kolay && orta && !zor) return 'kolay';
  if (!kolay && orta && zor) return 'zor';
  return 'karisik';
}

// Kova rozeti renkleri — panel puan rozetiyle (lib/zorlukSeviye.js) AYNI aile.
// ---- SEÇİM BUTONU STİLİ (2026-09-03, kullanıcı kararı: "seçimi yapılan özellik
// butonları farklı renkte ve belirgin olsun; özet metinlerine gerek yok").
// Tüm seçim grupları (kaynak, sınav, ders, zorluk profili, müfredat, zorluk ön
// ayarı, çıktı seçenekleri) aynı sınıfı kullanır: seçili = dolgulu mavi + kalın +
// halka + ✓ öneki; seçili değil = beyaz/gri, üstüne gelince mavi kenar.
// Yerel gun (YYYY-MM-DD) — tarih araligi kisayollari icin. `toISOString` UTC'ye
// kaydirir: TRT 03:00'ten once "bugun" DUNU secerdi. Basim aramasi (SoruEkleci)
// ve Konu Testi filtre kanali (KonuTestiPaneli) ayni yardimciyi tuketir.
export function gunStr(gunOnce = 0) {
  const d = new Date(Date.now() - gunOnce * 86400000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function secimSinifi(aktif, ek = '') {
  return `${ek} px-2.5 py-1 rounded-lg border-2 text-xs font-semibold transition-colors ${aktif
    ? 'bg-blue-600 border-blue-700 text-white shadow-sm ring-2 ring-blue-200'
    : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'}`;
}
export const SECILI_ISARET = '✓ ';

// ÇOK DERSLİ KONU TESTİ (2026-09-03): konuTest.dersler türetimi — eski kayıtlarda
// (v2 şablon/taslak, dersler alanı yok) tek `ders/kazanimlar/adet` üçlüsünden kurulur.
// ── NUMARA LİSTESİ → DERS PLANI (Basım Faz 1, 2026-10-09) ─────────────────────
// Yapıştırılan soru numaraları derslere ayrılır; her ders SABİT soru listesi (`sabitIds`) olur
// ve mevcut ders sabitleme yolundan ÖSYM çok testli deneme olarak basılır.

// "12, #45 40100-40103" → { nolar, gecersiz }. Aralık (a-b, a ≤ b) açılır; tekrarlar tekilleşir.
// Tavanı aşan aralık açılmaz, geçersiz sayılır (yanlışlıkla binlerce soru istenmesin).
export function numaraListesiAyristir(metin, tavan = 500) {
  const nolar = [];
  const gecersiz = [];
  const gorulen = new Set();
  const ekle = (n) => { if (!gorulen.has(n)) { gorulen.add(n); nolar.push(n); } };
  String(metin == null ? '' : metin).replace(/(\d+)\s*[-–]\s*#?(\d+)/g, '$1-$2')
    .split(/[,;\s]+/).forEach((parca) => {
      const t = parca.trim();
      if (!t) return;
      const a = /^#?(\d+)-#?(\d+)$/.exec(t);
      if (a) {
        const bas = Number(a[1]); const son = Number(a[2]);
        if (bas > 0 && son >= bas && son - bas < tavan) { for (let n = bas; n <= son; n += 1) ekle(n); } else gecersiz.push(t);
        return;
      }
      const m = /^#?(\d+)$/.exec(t);
      const n = m ? Number(m[1]) : NaN;
      if (!Number.isSafeInteger(n) || n <= 0) gecersiz.push(t); else ekle(n);
    });
  return { nolar, gecersiz };
}

// ÖSYM kitapçık ders sırası (ders ailesi). Paneldeki ders düğmesi sırası (Kimya, Fizik…) DEĞİL:
// ÖSYM Fen testi Fizik → Kimya → Biyoloji; AYT'de TDE-SB1 → SB2 → Matematik → Fen.
const OSYM_DERS_SIRASI = {
  TYT: ['turkce', 'tarih', 'cografya', 'felsefe', 'din', 'matematik', 'geometri', 'fizik', 'kimya', 'biyoloji'],
  AYT: ['edebiyat', 'tarih', 'cografya', 'felsefe', 'mantik', 'psikoloji', 'sosyoloji', 'din',
    'matematik', 'geometri', 'fizik', 'kimya', 'biyoloji'],
  // LGS (MEB): sözel oturum Türkçe → İnkılap → Din → İngilizce, sayısal Matematik → Fen.
  LGS: ['turkce', 'inkilap', 'din', 'ingilizce', 'matematik', 'fen'],
};
const _aile = (d) => String(d || '').replace(/^(tyt|ayt|lgs)_/, '');
const _puanBir = (v) => v != null && v !== '' && Number(v) === 1;
// Basılamayan sorunun nedeni (numara listesi + soru bankası testleri ortak; sunucu kuralıyla aynı).
// Seçili alıcının (yayineviId) kendi satılmış sorusu basılabilir.
export function basilamazNedeni(s, yayineviId = null) {
  if (s.durum !== 'onaylandi') return 'onaylı değil';
  if (_puanBir(s.zorluk_puan) || _puanBir(s.ogretmen_puani)) return '1 puanlı (hatalı işaretli)';
  if (s.kaynak === 'osym_capa') return 'ÖSYM çapa sorusu';
  if (satildiMi(s.satis) && !(yayineviId != null && s.satis && Number(s.satis.yayinevi_id) === Number(yayineviId))) {
    return 'satılmış / teslim edilmiş';
  }
  return null;
}

// `POST /api/sorular/batch {public_nolar}` yanıtı → { sinav, dersler, elenen, eksikKardes, bulunamayan }.
// - Sınav türü: soruların çoğunun ders AİLESİNİN bulunduğu tür (eşitlikte TYT). Plan dersi o türdeki
//   aile dersidir — sunucu sabit soruyu aileyle kabul eder (11 EA: tyt_tarih sorusu ayt_tarih altında).
// - Basılamayacak soru SESSİZCE düşmez, nedeniyle `elenen`e yazılır: onaysız, 1 puanlı, ÖSYM çapası,
//   başka alıcıya satılmış/teslim edilmiş (seçili alıcının kendi soruları basılır), bu türde dersi yok.
// - Bağlam çiftinin listede olan üyeleri yan yana, grup sırasıyla; listede olmayan kardeşler `eksikKardes`.
export function numaraListesiPlanla(sorular, { bulunamayan = [], yayineviId = null } = {}) {
  const liste = (Array.isArray(sorular) ? sorular : []).filter(Boolean);
  const turDersi = (tur, d) => (SINAV_DERSLERI[tur] || []).find((x) => _aile(x.key) === _aile(d)) || null;
  // Önce o türde yerleşebilen soru sayısı; çoğu aile iki türde de var (matematik, tarih…) →
  // eşitlikte soruların kendi öneki (tyt_/ayt_) çoğunluğu, o da eşitse TYT.
  const say = (tur) => liste.filter((s) => turDersi(tur, s.ders)).length;
  const onek = (o) => liste.filter((s) => String(s.ders || '').startsWith(`${o}_`)).length;
  // LGS (2026-10-20): liste tamamen lgs_* ise LGS (YKS ailesiyle karıştırılmaz — LGS ayrı havuz).
  const sinav = liste.length && liste.every((s) => String(s.ders || '').startsWith('lgs_')) ? 'LGS'
    : say('AYT') !== say('TYT') ? (say('AYT') > say('TYT') ? 'AYT' : 'TYT')
      : (onek('ayt') > onek('tyt') ? 'AYT' : 'TYT');
  const elenen = [];
  const uygun = [];
  for (const s of liste) {
    const no = Number(s.public_no);
    const d = turDersi(sinav, s.ders);
    let neden = basilamazNedeni(s, yayineviId);
    if (!neden && !d) neden = `${sinav} denemesinde dersi yok`;
    if (neden) elenen.push({ no, neden }); else uygun.push({ s, ders: d.key });
  }
  const grup = (s) => { const g = s.kaynak_detay && s.kaynak_detay.baglam_grubu; return typeof g === 'string' && g ? g : null; };
  const sira = (s) => { const n = parseInt(s.kaynak_detay && s.kaynak_detay.grup_sira, 10); return Number.isFinite(n) ? n : 0; };
  const dersler = [];
  for (const a of OSYM_DERS_SIRASI[sinav]) {
    const ds = uygun.filter((u) => _aile(u.ders) === a);
    if (!ds.length) continue;
    const dizi = []; const yerlesti = new Set();
    for (const u of ds) {
      if (yerlesti.has(u.s)) continue;
      const g = grup(u.s);
      const birim = g ? ds.filter((v) => grup(v.s) === g).map((v) => v.s).sort((x, y) => sira(x) - sira(y)) : [u.s];
      birim.forEach((x) => { yerlesti.add(x); dizi.push(x); });
    }
    dersler.push({ ders: ds[0].ders, kazanimlar: [], adet: dizi.length,
      sabitIds: dizi.map((x) => x.id), sabitNolar: dizi.map((x) => Number(x.public_no)) });
  }
  const eksikKardes = uygun
    .filter((u) => Array.isArray(u.s.grup_eksik_uyeler) && u.s.grup_eksik_uyeler.length > 0)
    .map((u) => ({ no: Number(u.s.public_no), kardesler: u.s.grup_eksik_uyeler.map((k) => Number(k.public_no)) }));
  return { sinav, dersler, elenen, eksikKardes, bulunamayan: (bulunamayan || []).map(Number), toplam: uygun.length };
}

// ── SABİT LİSTE DÜZENLEYİCİ (Basım Faz 2, 2026-10-09) ────────────────────────
// `detay`: { [id]: POST /api/sorular/batch satırı }. Kova eşikleri sunucuyla AYNI
// (lib/denemeFormat ZORLUK_KOVA: kolay 2-4 · orta 5-6 · zor 7-10).
export function kovaAdi(puan) {
  const p = Number(puan);
  if (puan == null || puan === '' || !Number.isFinite(p)) return null;
  return p <= 4 ? 'kolay' : (p <= 6 ? 'orta' : 'zor');
}
const _grupAdi = (s) => { const g = s && s.kaynak_detay && s.kaynak_detay.baglam_grubu; return typeof g === 'string' && g ? g : null; };

// Ardışık aynı bağlam grubundaki kimlikler TEK BİRİM (çift birlikte taşınır / çıkar / değişir).
export function sabitBirimleri(ids, detay = {}) {
  const birimler = [];
  (ids || []).forEach((id) => {
    const g = _grupAdi(detay[id]);
    const son = birimler[birimler.length - 1];
    if (g && son && _grupAdi(detay[son[0]]) === g) son.push(id); else birimler.push([id]);
  });
  return birimler;
}

// Ders bloğu göstergesi: ÖÇ çeşitliliği + tekrar eden ÖÇ, zorluk kovası, harf payı (tavan %30).
export function sabitOzet(ids, detay = {}) {
  const satirlar = (ids || []).map((id) => detay[id]).filter(Boolean);
  const oc = {}; const kova = { kolay: 0, orta: 0, zor: 0, bilinmiyor: 0 }; const harf = {};
  satirlar.forEach((s) => {
    if (s.kazanim_kodu) oc[s.kazanim_kodu] = (oc[s.kazanim_kodu] || 0) + 1;
    kova[kovaAdi(s.zorluk_puan) || 'bilinmiyor'] += 1;
    const h = String(s.dogru_cevap || '').trim().toUpperCase();
    if (/^[A-E]$/.test(h)) harf[h] = (harf[h] || 0) + 1;
  });
  const n = (ids || []).length;
  const harfTavan = Math.max(1, Math.ceil(n * 0.3));
  return {
    n, yuklu: satirlar.length, ocSayisi: Object.keys(oc).length,
    ocTekrar: Object.entries(oc).filter(([, k]) => k > 1).map(([kod, k]) => ({ kod, n: k })),
    kova, harf, harfTavan, harfAsim: Object.keys(harf).filter((h) => harf[h] > harfTavan).sort(),
  };
}

// ÖSYM test bloğu (sunucu _blokKodu ile aynı mantık): AYT'de Edebiyat varsa Tarih/Coğrafya TDE-SB1'de.
export function osymTestAdi(sinav, ders, dersler = []) {
  const a = _aile(ders);
  // LGS: her ders kendi testi (backend dersBaslik LGS/* blokları).
  if (sinav === 'LGS') return (DERS_BASLIK[`lgs_${a}`] || {}).ad || a;
  if (sinav === 'AYT') {
    if (a === 'edebiyat') return 'Türk Dili ve Edebiyatı-Sosyal Bilimler-1';
    if (a === 'tarih' || a === 'cografya') {
      return (dersler || []).some((d) => _aile(d.ders || d) === 'edebiyat')
        ? 'Türk Dili ve Edebiyatı-Sosyal Bilimler-1' : 'Sosyal Bilimler-2';
    }
    if (['felsefe', 'mantik', 'psikoloji', 'sosyoloji', 'din'].includes(a)) return 'Sosyal Bilimler-2';
    if (a === 'matematik' || a === 'geometri') return 'Matematik';
    return 'Fen Bilimleri';
  }
  if (a === 'turkce') return 'Türkçe';
  if (['tarih', 'cografya', 'felsefe', 'din'].includes(a)) return 'Sosyal Bilimler';
  if (a === 'matematik' || a === 'geometri') return 'Temel Matematik';
  return 'Fen Bilimleri';
}

// Deneme düzeyi: test başına soru sayısı + harf dengesi (yalnız SABİT dersler). Sunucuyla aynı ölçüt
// (lib/harfDengesi.bant, 2026-10-10): her harf [⌊n/k⌋−1, ⌈n/k⌉+1] içinde (k şık sayısı: YKS 5, LGS 4)
// ve %30 tavanının altında.
export function testHarfOzeti(sinav, dersler, detay = {}) {
  const testler = new Map();
  (dersler || []).filter((d) => Array.isArray(d.sabitIds) && d.sabitIds.length > 0).forEach((d) => {
    const ad = osymTestAdi(sinav, d.ders, dersler);
    if (!testler.has(ad)) testler.set(ad, { ad, n: 0, harf: {} });
    const t = testler.get(ad);
    d.sabitIds.forEach((id) => {
      t.n += 1;
      const h = String((detay[id] && detay[id].dogru_cevap) || '').trim().toUpperCase();
      if (sikHarfleri(sinav).includes(h)) t.harf[h] = (t.harf[h] || 0) + 1;
    });
  });
  return [...testler.values()].map((t) => {
    const harfler = sikHarfleri(sinav);
    const k = harfler.length;
    const tavan = Math.min(Math.ceil(t.n / k) + 1, Math.max(1, Math.ceil(t.n * 0.3)));
    const alt = t.n >= k ? Math.max(0, Math.floor(t.n / k) - 1) : 0;
    return { ...t, tavan, alt, asim: harfler.filter((h) => (t.harf[h] || 0) > tavan || (t.harf[h] || 0) < alt) };
  });
}

// Liste işlemleri — ders nesnesi {sabitIds, sabitNolar, adet} döner (adet = soru sayısı).
// Numara dizisi yoksa detaydan türetilir (eski "Soruları sabitle" blokları yalnız kimlik taşır).
function _sabitYaz(d, ids, detay) {
  const nolar = ids.map((id) => {
    if (detay[id] && detay[id].public_no != null) return Number(detay[id].public_no);
    const i = (d.sabitIds || []).indexOf(id);
    return i >= 0 && Array.isArray(d.sabitNolar) ? d.sabitNolar[i] : null;
  });
  return { ...d, sabitIds: ids, sabitNolar: nolar, adet: ids.length };
}
export function sabitTasi(d, birimNo, yon, detay = {}) {
  const b = sabitBirimleri(d.sabitIds, detay);
  const j = birimNo + yon;
  if (birimNo < 0 || birimNo >= b.length || j < 0 || j >= b.length) return d;
  [b[birimNo], b[j]] = [b[j], b[birimNo]];
  return _sabitYaz(d, b.flat(), detay);
}
export function sabitCikar(d, birimNo, detay = {}) {
  const b = sabitBirimleri(d.sabitIds, detay);
  return _sabitYaz(d, b.filter((_, i) => i !== birimNo).flat(), detay);
}
// yeniUyeler: aday birimin satırları ({id, public_no}); birimin yerine aynı konumda girer.
export function sabitDegistir(d, birimNo, yeniUyeler, detay = {}) {
  const b = sabitBirimleri(d.sabitIds, detay);
  if (birimNo < 0 || birimNo >= b.length) return d;
  const ek = {};
  (yeniUyeler || []).forEach((u) => { ek[u.id] = { ...(detay[u.id] || {}), public_no: u.public_no }; });
  b[birimNo] = (yeniUyeler || []).map((u) => u.id);
  return _sabitYaz(d, b.flat(), { ...detay, ...ek });
}
export function sabitEkle(d, yeniIds, yeniNolar, detay = {}) {
  const ek = {};
  (yeniIds || []).forEach((id, i) => { ek[id] = { ...(detay[id] || {}), public_no: yeniNolar[i] }; });
  return _sabitYaz(d, [...(d.sabitIds || []), ...(yeniIds || [])], { ...detay, ...ek });
}

// ── DENGELİ SEÇİM (Basım Faz 3, 2026-10-09) ──────────────────────────────────
// Zorluk profili → kova oranı [kolay, orta, zor]; "Karışık" (kısıtsız) dengeli seçimde 3:4:3.
export function profilOranlari(profilId) {
  const p = ZORLUK_PROFILLERI.find((x) => x.id === profilId);
  if (!p || !p.agirlik) return [3, 4, 3];
  return [p.agirlik.kolay, p.agirlik.orta, p.agirlik.zor];
}
const _sabitMi = (d) => Array.isArray(d.sabitIds) && d.sabitIds.length > 0;
// Seçilecek dersler: sabit OLMAYANLAR + daha önce dengeli seçimle dolanlar (`dengeli` = kaynak ders;
// Matematik'in geometri parçası aynı kaynağa sayılır). Elle sabitlenen ders (numara listesi / "Soruları
// sabitle") dokunulmaz, numaraları dışlanır. → { istek:[{ders, adet}], matematikGeometri, kazanimYokSayildi }
export function dengeliIstek(dersler) {
  const istek = [];
  let kazanimYokSayildi = false;
  (dersler || []).forEach((d) => {
    const anahtar = d.dengeli || (_sabitMi(d) ? null : d.ders);
    if (!anahtar) return;
    const adet = _sabitMi(d) ? d.sabitIds.length
      : (Array.isArray(d.kazanimlar) && d.kazanimlar.length > 0 ? d.kazanimlar.reduce((a, k) => a + (Number(k.adet) || 0), 0) : Number(d.adet) || 0);
    if (!_sabitMi(d) && Array.isArray(d.kazanimlar) && d.kazanimlar.length > 0) kazanimYokSayildi = true;
    const v = istek.find((x) => x.ders === anahtar);
    if (v) v.adet += adet; else istek.push({ ders: anahtar, adet });
  });
  // Matematik geometriyi ancak listede AYRI bir Geometri dersi yoksa kapsar (yinelenen ders olmasın).
  const geometriAyri = (dersler || []).some((d) => _aile(d.ders) === 'geometri'
    && !(d.dengeli && _aile(d.dengeli) === 'matematik'));
  return { istek: istek.filter((x) => x.adet > 0), matematikGeometri: !geometriAyri, kazanimYokSayildi };
}
// Yanıtı uygular: istenen anahtarların dersleri, yanıttaki parçalarla (aynı konumda) değişir.
export function dengeliUygula(dersler, istek, yanit) {
  const anahtarlar = new Set((istek || []).map((x) => x.ders));
  const eklendi = new Set();
  const yeni = [];
  (dersler || []).forEach((d) => {
    const anahtar = d.dengeli || (_sabitMi(d) ? null : d.ders);
    if (anahtar && anahtarlar.has(anahtar)) {
      if (!eklendi.has(anahtar)) {
        (yanit.dersler || []).filter((e) => e.kaynak_ders === anahtar).forEach((e) => yeni.push({
          ders: e.ders, kazanimlar: [], adet: e.adet, sabitIds: e.sabitIds, sabitNolar: e.sabitNolar, dengeli: anahtar,
        }));
        eklendi.add(anahtar);
      }
      return;
    }
    yeni.push(d);
  });
  return yeni;
}

export function konuTestDersleri(kt) {
  if (!kt) return [];
  if (Array.isArray(kt.dersler)) return kt.dersler.filter(d => d && d.ders);
  if (kt.ders) return [{ ders: kt.ders, kazanimlar: Array.isArray(kt.kazanimlar) ? kt.kazanimlar : [], adet: Number(kt.adet) || 20 }];
  return [];
}
// konuTest'i her iki alanla tutarlı hale getirir (dersler ⇄ ders/kazanimlar/adet).
export function konuTestNormalize(kt) {
  const dersler = konuTestDersleri(kt);
  const ilk = dersler[0] || null;
  return { ...kt, dersler, ders: ilk ? ilk.ders : '', kazanimlar: ilk ? ilk.kazanimlar : [], adet: ilk ? ilk.adet : (Number(kt && kt.adet) || 20) };
}

export const KOVA_RENK = {
  kolay: 'bg-emerald-100 text-emerald-800',
  orta: 'bg-amber-100 text-amber-800',
  zor: 'bg-indigo-100 text-indigo-800',
};

export function dostHataMesaji(hata) {
  if (!hata) return null;
  const s = String(hata);
  for (const [k, v] of Object.entries(HATA_MESAJLARI)) {
    if (s.includes(k)) return v;
  }
  if (s.includes('utf-8') || s.includes('codec')) {
    return 'Bir sorunun görseli özel karakter içerdiği için render edilemedi. "✕ Bunları Çıkar" ile devam edin.';
  }
  return s;
}

// TOPLU COKUS ayrimi (2026-08-18).
export function topluCokusMu(data) {
  if (!data || data.basarili !== false) return false;
  const atlanan = Number(data.atlanan_sayisi || 0);
  const donen = Number(data.soru_sayisi || 0);
  return atlanan > 0 && donen > 0 && atlanan >= donen;
}

// pdflatex log kuyrugundan ASIL hata satirini cikarir.
export function latexHataOzeti(detay) {
  if (!detay) return null;
  const satirlar = String(detay).split(/\r?\n/);
  const i = satirlar.findIndex(s => s.startsWith('! '));
  if (i === -1) return null;
  const parcalar = [satirlar[i].trim()];
  for (let j = i + 1; j < Math.min(i + 4, satirlar.length); j++) {
    const s = satirlar[j].trim();
    if (s.startsWith('l.') || s.startsWith('<argument>')) parcalar.push(s);
  }
  return parcalar.join('  ·  ');
}

// Basarisiz basim yanitini tek yerden mesaja cevirir.
export function basimHataMesaji(data, varsayilan) {
  if (data && data.red_nedeni === 'layout_ihlal') {
    const hatalarStr = Array.isArray(data.hatalar) ? data.hatalar.join(' | ') : '';
    return `KURALLAR §16 PDF KITAPCIK DUZENI ihlali: ${hatalarStr}. `
      + `Sayfa: ${data.sayfa_sayisi || '?'} (beklenen ${data.beklenen_sayfa_aralik || '?'}). `
      + 'Soru sayısını ayarlayın ya da "Yine de üret" ile §16 kapısını bu basım için atlayın.';
  }
  if (data && data.red_nedeni === 'ders_sizintisi') {
    const sizan = Array.isArray(data.sizan_public_no) ? data.sizan_public_no.join(', ') : '';
    return `Ders sızıntısı kapısı: istenen ders "${data.istenen_ders || '?'}" ama `
      + `${data.sizan_sayisi || '?'} soru başka dersten geldi${sizan ? ` (#${sizan})` : ''}. `
      + 'PDF üretilmedi.';
  }
  if (data && data.red_nedeni === 'deneme_havuz_yetersiz') {
    const eksik = Array.isArray(data.eksik_dersler)
      ? data.eksik_dersler.map(e => `${e.ders} ${e.havuz}/${e.kota}`).join(', ') : '';
    return `Tam deneme için havuz yetersiz${eksik ? `: ${eksik}` : ''}. `
      + 'Onay bekleyen soruları panelden onaylayarak havuzu genişletebilirsiniz.';
  }
  if (data && data.red_nedeni === 'deneme_kapsam_ihlali') {
    return `Deneme kapsam kapısı: ${data.hata || 'format dışı ders/kota sapması'}. PDF üretilmedi.`;
  }
  if (data && Array.isArray(data.konu_hatalari) && data.konu_hatalari.length > 0) {
    return `Konu bazlı zorluk: ${data.konu_hatalari.join('; ')}. Konu adını listeden seçin.`;
  }
  if (topluCokusMu(data)) {
    return `Hiçbir soru derlenemedi (${data.atlanan_sayisi}/${data.soru_sayisi}). `
      + 'Bu, tek tek sorulardan değil ortak dizgi katmanından kaynaklanıyor olabilir — '
      + 'çıkarılacak soru yok. Teknik ayrıntı aşağıda.';
  }
  return dostHataMesaji(data && data.hata) || varsayilan;
}

// Banner'da gosterilecek ham teknik ayrinti.
export function basimHataDetayi(data) {
  if (!data) return null;
  const ozet = latexHataOzeti(data.hata_detay)
    || (Array.isArray(data.atlanan_sorular)
      ? data.atlanan_sorular.map(a => latexHataOzeti(a.hata_detay)).find(Boolean)
      : null);
  if (ozet) return ozet;
  if (Array.isArray(data.atlanan_sorular) && data.atlanan_sorular.length > 0) {
    return data.atlanan_sorular
      .slice(0, 5)
      .map(a => `#${a.public_no} ${a.konu || ''} — ${a.hata || 'bilinmeyen'}`)
      .join('\n');
  }
  return null;
}

export function tikzBoyutTemizle(svg) {
  if (!svg) return '';
  // SVG'nin sabit width/height (örn 595pt) attribute'larını "100%" + "auto"a çevir.
  return svg
    .replace(/<svg([^>]*?)\swidth="[^"]*"/i, '<svg$1 width="100%"')
    .replace(/<svg([^>]*?)\sheight="[^"]*"/i, '<svg$1 height="auto"');
}

export function dosyaAdiUret(urun, ders = '', ekstra = '') {
  const tarih = new Date().toISOString().slice(0, 10);
  const ek = ekstra ? `_${ekstra}` : '';
  const d = ders ? `_${String(ders).replace(/[^a-z0-9_]/gi, '')}` : '';
  return `basim_${urun}${d}${ek}_${tarih}.pdf`;
}

// Okul basımı dosya adı (2026-10-13, kullanıcı isteği): "PiriReis_9A_Soru_13_10_26".
// Okul adından "Anadolu" / "Lisesi" atılır (Fen, İmam Hatip kalır: "Adana Fen" ≠ "Adana"), ASCII'ye çevrilir.
const _ASCII = { ç: 'c', Ç: 'C', ğ: 'g', Ğ: 'G', ı: 'i', İ: 'I', ö: 'o', Ö: 'O', ş: 's', Ş: 'S', ü: 'u', Ü: 'U' };
export function okulKisaAd(ad) {
  return String(ad || '').split(/\s+/)
    .filter(w => !['anadolu', 'lisesi'].includes(w.toLocaleLowerCase('tr')))
    .map(w => w.replace(/[çÇğĞıİöÖşŞüÜ]/g, c => _ASCII[c]).replace(/[^A-Za-z0-9]/g, ''))
    .join('');
}

export function okulDosyaAdi({ okulAd, sinif = null, tur = '', tip, uzanti = 'pdf', tarih = new Date() }) {
  const iki = (n) => String(n).padStart(2, '0');
  const gun = `${iki(tarih.getDate())}_${iki(tarih.getMonth() + 1)}_${iki(tarih.getFullYear() % 100)}`;
  return [okulKisaAd(okulAd), `${sinif || ''}${tur || ''}`, tip, gun].filter(Boolean).join('_') + `.${uzanti}`;
}

export function fisherYatesShuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// localStorage helper
export function lsRead(key, fallback = null) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
}
export function lsWrite(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); return true; }
  catch { return false; }
}

/**
 * Eski taslak/şablonu (v1: aktifFormat + formatAyarlar + filtreAyarlar) v2 şemaya
 * çevirir (Faz C). Depo YERİNDE DEĞİŞTİRİLMEZ — yükleme anında dönüştürülür,
 * yeni kayıtlar v2 yazar. Eşleme:
 *   denemeKapsam=tam + TYT → 'tyt' · + AYT → 'ayt_sayisal' · diğer her şey → 'konu'
 *   filtreAyarlar.{mufredat,ders,adet,kazanim_kodu,zorluk} → konuTest
 *   ölü alanlar (kağıt, header/footer, yerleşim, sütun, kitapçık türü, kapak/OMR,
 *   görsel boyutu, çözüm alanı, soru bankası konu seçimi...) DÜŞER.
 */
export function taslakEsle(t) {
  if (!t || typeof t !== 'object') return null;
  if (t.surum === TASLAK_SURUM) return t;
  const fa = t.formatAyarlar || {};
  const fi = t.filtreAyarlar || {};
  let urun = 'konu';
  if (fa.denemeKapsam === 'tam' && fa.denemeAltMod === 'TYT') urun = 'tyt';
  else if (fa.denemeKapsam === 'tam' && fa.denemeAltMod === 'AYT') urun = 'ayt_sayisal';
  const ders = typeof fi.ders === 'string' ? fi.ders : '';
  const adetHam = Math.floor(Number(fi.adet));
  const adet = Number.isFinite(adetHam) && adetHam >= 1 ? Math.min(SORU_ADET_MAX, adetHam) : BASLANGIC_KONU_TEST.adet;
  const kazanimlar = (typeof fi.kazanim_kodu === 'string' && fi.kazanim_kodu.trim())
    ? [{ kod: fi.kazanim_kodu.trim(), ad: fi.kazanim_kodu.trim(), adet }]
    : [];
  return {
    surum: TASLAK_SURUM,
    ad: t.ad,
    tarih: t.tarih,
    eskiSablon: true,
    urun,
    kaynakModu: t.kaynakModu === 'secili' ? 'secili' : 'filtre',
    mufredat: typeof fi.mufredat === 'string' ? fi.mufredat : '',
    konuTest: {
      sinav: ders.startsWith('ayt_') ? 'AYT' : 'TYT',
      ders,
      dersler: ders ? [{ ders, kazanimlar, adet }] : [],
      kazanimlar,
      adet,
      zorlukProfil: zorlukEnumdanProfil(fi.zorluk),
    },
    dersZorluk: (t.dersZorluk && typeof t.dersZorluk === 'object') ? t.dersZorluk : {},
    konuZorluk: {},
    cevapAnahtariGoster: fa.cevapAnahtariGoster !== false,
    cozumDosyasiAyri: t.cozumDosyasiAyri === true,
    kurum: typeof fa.kurum === 'string' ? fa.kurum : '',
    sinavTarihi: typeof fa.sinavTarihi === 'string' ? fa.sinavTarihi : '',
    oncekiDenemeHaric: false,
    // 2026-09-15: anlam değişti — eski "yayınevine verilenler dahil" bayrağı
    // TAŞINMAZ. Yeni `ayniYayineviDahil` yalnız seçili yayınevine bağlıdır ve
    // yayınevi taslak/şablonda saklanmaz → her yüklemede kapalı başlar.
    ayniYayineviDahil: false,
    denemeManifest: t.denemeManifest || null,
    seciliIds: Array.isArray(t.seciliIds) ? t.seciliIds : [],
    // Taşınamayan eski konu seçimi (soru bankası "Çoklu Konu Seçimi") — bilgi.
    eskiKonuSecimi: Array.isArray(t.seciliKonular) && t.seciliKonular.length > 0,
  };
}

// Sınav süresi elle (2026-10-10): 10-600 arası tam sayı dakika; boş/geçersiz → gönderilmez,
// sunucu ÖSYM oranından hesaplar. Sunucu doğrulayıcısıyla (pipeline _basimGovdeDogrula) aynı sınır.
export const SINAV_SURESI_MIN = 10;
export const SINAV_SURESI_MAX = 600;
export function sinavSuresiGecerli(v) {
  const t = String(v == null ? '' : v).trim();
  if (!/^\d+$/.test(t)) return false;
  const n = Number(t);
  return n >= SINAV_SURESI_MIN && n <= SINAV_SURESI_MAX;
}

// Sınav adı elle (2026-10-10): kapakta büyük harfle basılır; sunucu sınırıyla aynı uzunluk.
export const SINAV_ADI_MAX = 80;

// Ders / öğrenme çıktısı başına soru sayısı tavanı (2026-10-11, kullanıcı isteği: 100 → 250).
// Sunucu konu testi / genel deneme isteğinde 500'e kadar kabul eder; dengeli seçim ucu bu değerle aynı.
export const SORU_ADET_MAX = 250;

// SORU BANKASI ARKA PLAN İŞİ (2026-10-22): 1000 soruluk kitap 6-12 dk sürer, Apache 300 sn'de keser. İstek `?is=1`
// ile gider; sunucu 202 + is_id dönerse sonuç hazır olana kadar /basim/is/:id sorgulanır (geçen saniye
// `ilerleme`ye). 202 değilse (doğrulama reddi, eski sunucu) yanıt olduğu gibi döner.
export async function basimIsBekle(apiUrl, r, basliklar, { aralikMs = 3000, ilerleme = null } = {}) {
  const ilk = await r.json();
  if (r.status !== 202 || !ilk.is_id) return ilk;
  for (let hata = 0; ;) {
    await new Promise((s) => setTimeout(s, aralikMs));
    try {
      const s = await fetch(`${apiUrl}/api/boru-hatti/basim/is/${ilk.is_id}`, { headers: basliklar });
      const d = await s.json();
      hata = 0;
      if (d.durum !== 'calisiyor') return d;
      if (ilerleme) ilerleme(d.gecen_sn);
    } catch (e) {
      if (++hata >= 10) throw e;      // geçici ağ kopması 10 kez tolere edilir; iş sunucuda sürer
    }
  }
}
